/**
 * Plan card content as the server stores and validates it (apps/server/planCards.ts).
 * Mirrored here by hand: the client renders it, the admin editors build it, and the
 * server refuses anything that does not match.
 */

export const SUPPORTED_LANGUAGES = ['en', 'fr'] as const
export type CardLanguage = (typeof SUPPORTED_LANGUAGES)[number]

export const CARD_TOKENS = ['planName', 'price', 'interval', 'period', 'minutes', 'modules'] as const
export type CardToken = (typeof CARD_TOKENS)[number]

export const CARD_VARIANTS = ['default', 'featured', 'muted'] as const
export const BADGE_TONES = ['highlight', 'outline', 'subtle'] as const
export const BADGE_POSITIONS = ['top', 'corner'] as const
export const CTA_STYLES = ['primary', 'secondary'] as const
export const PAYWALL_ICONS = ['refresh', 'shield', 'clock', 'headset', 'chart', 'message'] as const

export type LocalizedText = { en: string } & Partial<Record<CardLanguage, string>>

export interface CardBadge {
  text: LocalizedText
  tone: (typeof BADGE_TONES)[number]
  position: (typeof BADGE_POSITIONS)[number]
}

export interface FeatureItem {
  text: LocalizedText
  tag?: LocalizedText
}

export type TextBlockType = 'title' | 'tagline' | 'featuresLabel'

export type CardBlock =
  | { id: string; type: TextBlockType; text: LocalizedText }
  | { id: string; type: 'price'; periodLabel?: LocalizedText; note?: LocalizedText }
  | { id: string; type: 'divider' }
  | { id: string; type: 'features'; items: FeatureItem[] }
  | { id: string; type: 'cta'; label: LocalizedText; style: (typeof CTA_STYLES)[number]; arrow: boolean }

export type CardBlockType = CardBlock['type']

export interface CardContent {
  version: 1
  variant: (typeof CARD_VARIANTS)[number]
  badge: CardBadge | null
  blocks: CardBlock[]
}

/** Text fields a plan may override, per block id. */
export type BlockOverride = Partial<{
  text: LocalizedText
  label: LocalizedText
  periodLabel: LocalizedText
  note: LocalizedText
  items: FeatureItem[]
}>

/** `badge` absent keeps the template's; null removes it. */
export interface CardOverrides {
  badge?: CardBadge | null
  blocks?: Record<string, BlockOverride>
}

export interface ReassuranceItem {
  icon: (typeof PAYWALL_ICONS)[number]
  title: LocalizedText
  text: LocalizedText
}

export interface PaywallContent {
  version: 1
  title?: LocalizedText
  intro?: LocalizedText
  reassurance: ReassuranceItem[]
  footnote?: LocalizedText
}

/** What a card needs to know about the plan it draws. */
export interface CardPlan {
  name: string
  priceAmount: number
  currency: string
  interval: PlanInterval
  /** False: a one-period pass. Changes the period wording, not the price. */
  renews: boolean
  includedSeconds: number
  moduleNames: string[]
}

export type PlanInterval = 'month' | 'quarter' | 'year'

/** The text fields each block type lets a plan override. */
export const OVERRIDABLE_FIELDS: Record<CardBlockType, (keyof BlockOverride)[]> = {
  title: ['text'],
  tagline: ['text'],
  featuresLabel: ['text'],
  price: ['periodLabel', 'note'],
  features: ['items'],
  cta: ['label'],
  divider: [],
}
